import { Theme } from '@eversports/react-components/design-tokens/theme'
import Flex from '@eversports/react-components/primitives/Flex'
import { SimpleText } from '@eversports/react-components/primitives/Text'
import { assertNever } from '@eversports/utility-types'
import { makeStyles } from '@mui/styles'
import React from 'react'

import { Localized } from '../../localization/react'
import { isPurchaseOptionPurchasable, PurchaseOption, TicketPriceType } from '../../typings/Product'

interface Props {
  product: PurchaseOption
}

const useStyles = makeStyles((theme: Theme) => ({
  membershipLabelTag: {
    backgroundColor: theme.palette.secondary.main,
    fontWeight: 700,
    padding: 4,
  },
  corporateLabelTag: {
    backgroundColor: theme.palette.warning.main,
    fontWeight: 700,
    padding: 4,
  },
  ticketLabelTag: {
    backgroundColor: theme.palette.grey[400],
    fontWeight: 700,
    padding: 4,
  },
  hideOnMobile: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
}))

const PurchaseOptionName: React.FC<React.PropsWithChildren<Props>> = ({ product }) => {
  const classes = useStyles()

  switch (product.__typename) {
    case 'Membership':
      return (
        <Flex flexDirection="column">
          <Flex
            flexDirection={['column', 'row']}
            horizontalSpacing={['none', 'dense']}
            alignItems={['flex-start', 'center']}
          >
            <SimpleText sx={{ fontWeight: 'bold', wordBreak: 'break-word' }} data-testid="membership-purchase-option">
              {product.name}
            </SimpleText>
            <span className={classes.membershipLabelTag}>
              <SimpleText sx={{ fontWeight: 'bold', color: 'white' }} size="x-small" data-testid="membership-label">
                <Localized id="membership" />
              </SimpleText>
            </span>
          </Flex>
          {product.description && <SimpleText className={classes.hideOnMobile}>{product.description}</SimpleText>}
        </Flex>
      )
    case 'Ticket':
      const isTicketPurchasable = product.productPurchaseInformation.purchasable
      return (
        <Flex flexDirection="column">
          <Flex
            flexDirection={['column', 'row']}
            horizontalSpacing={['none', 'dense']}
            alignItems={['flex-start', 'center']}
          >
            <SimpleText sx={{ fontWeight: 'bold', wordBreak: 'break-word' }} data-testid="ticket-purchase-option">
              {product.name}
            </SimpleText>
            {isTicketPurchasable && product.currentPriceType === TicketPriceType.TICKET_PRICE_TYPE_EARLY_BIRD && (
              <span className={classes.ticketLabelTag}>
                <SimpleText size="x-small" sx={{ color: 'white', fontWeight: 'bold' }} data-testid="ticket-label">
                  <Localized id="ticket-early-bird-price" />
                </SimpleText>
              </span>
            )}
            {isTicketPurchasable && product.currentPriceType === TicketPriceType.TICKET_PRICE_TYPE_LAST_MINUTE && (
              <span className={classes.ticketLabelTag}>
                <SimpleText size="x-small" sx={{ color: 'white', fontWeight: 'bold' }} data-testid="ticket-label">
                  <Localized id="ticket-last-minute-price" />
                </SimpleText>
              </span>
            )}
          </Flex>
          {!isTicketPurchasable && product.productPurchaseInformation.additionalInformation}
          {isTicketPurchasable &&
            product.currentPriceType === TicketPriceType.TICKET_PRICE_TYPE_EARLY_BIRD &&
            product.earlyBirdUntil && (
              <Localized id="ticket-early-bird-until" params={{ earlyBirdUntil: new Date(product.earlyBirdUntil) }} />
            )}
        </Flex>
      )
    case 'Card':
      return (
        <Flex flexDirection="column">
          <Flex
            flexDirection={['column', 'row']}
            horizontalSpacing={['none', 'dense']}
            alignItems={['flex-start', 'center']}
          >
            <SimpleText sx={{ fontWeight: 'bold', wordBreak: 'break-word' }} data-testid="card-purchase-option">
              {product.name}
            </SimpleText>
            {product.isCorporatePrice && (
              <span className={classes.corporateLabelTag}>
                <SimpleText sx={{ fontWeight: 'bold', color: 'text' }} size="x-small" data-testid="corporate-label">
                  <Localized id="corporate-benefit" />
                </SimpleText>
              </span>
            )}
          </Flex>
          {!isPurchaseOptionPurchasable(product) &&
            product.productPurchaseInformation &&
            product.productPurchaseInformation.additionalInformation}
        </Flex>
      )
    case 'SinglePurchase':
      return (
        <SimpleText sx={{ fontWeight: 'bold', wordBreak: 'break-word' }} data-testid="single-purchase-option">
          {product.name}
        </SimpleText>
      )
    default:
      return assertNever(product)
  }
}

export default PurchaseOptionName
